import React, { useState } from 'react';
import { useDashboard } from '../contexts/DashboardContext';

interface ResetModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ResetModal: React.FC<ResetModalProps> = ({ isOpen, onClose }) => {
  const { loadTemplate, removeAllWindows, templates } = useDashboard();
  const [showTemplates, setShowTemplates] = useState(false);

  if (!isOpen) return null;

  const handleResetToTemplate = () => {
    setShowTemplates(true);
  };

  const handleSelectTemplate = (templateSlug: string) => {
    loadTemplate(templateSlug);
    onClose();
    setShowTemplates(false);
  };

  const handleRemoveAllWindows = () => {
    if (window.confirm('Are you sure you want to remove all windows from this dashboard?')) {
      removeAllWindows();
      onClose();
    }
  };

  if (showTemplates) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
        <div
          className="bg-dark-surface border border-dark-border rounded-lg w-full max-w-md overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border">
            <h2 className="text-lg font-semibold text-gray-100">Select Template</h2>
            <button
              onClick={() => {
                setShowTemplates(false);
                onClose();
              }}
              className="text-gray-400 hover:text-gray-300 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-6 space-y-3">
            {templates.length === 0 ? (
              <div className="text-sm text-gray-400 text-center py-4">Loading templates...</div>
            ) : (
              templates.map((template) => (
                <button
                  key={template.slug}
                  onClick={() => handleSelectTemplate(template.slug)}
                  className="w-full flex flex-col items-start p-4 bg-dark-bg hover:bg-dark-surface-light border border-dark-border rounded-lg transition-colors text-left"
                >
                  <div className="text-sm font-medium text-gray-100 mb-1">{template.name}</div>
                  <div className="text-xs text-gray-400">{template.description}</div>
                </button>
              ))
            )}
          </div>
          <div className="px-6 py-4 border-t border-dark-border flex justify-end">
            <button
              onClick={() => setShowTemplates(false)}
              className="px-4 py-2 text-sm text-gray-300 bg-dark-bg hover:bg-dark-surface-light border border-dark-border rounded transition-colors"
            >
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-dark-surface border border-dark-border rounded-lg w-full max-w-md overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border">
          <h2 className="text-lg font-semibold text-gray-100">Reset Dashboard</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-300 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-3">
          {/* Reset to Template */}
          <button
            onClick={handleResetToTemplate}
            className="w-full flex flex-col items-start p-4 bg-dark-bg hover:bg-dark-surface-light border border-dark-border rounded-lg transition-colors text-left"
          >
            <div className="flex items-center gap-2 mb-1">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
              </svg>
              <span className="text-sm font-medium text-gray-100">Reset to a Template</span>
            </div>
            <p className="text-xs text-gray-400">Choose a predefined dashboard layout</p>
          </button>

          {/* Remove All Windows */}
          <button
            onClick={handleRemoveAllWindows}
            className="w-full flex flex-col items-start p-4 bg-dark-bg hover:bg-dark-surface-light border border-dark-border rounded-lg transition-colors text-left"
          >
            <div className="flex items-center gap-2 mb-1">
              <svg className="w-4 h-4 text-bear-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="text-sm font-medium text-bear-red">Remove All Windows</span>
            </div>
            <p className="text-xs text-gray-400">Clear the entire dashboard</p>
          </button>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-dark-border flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-300 bg-dark-bg hover:bg-dark-surface-light border border-dark-border rounded transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
